import { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { fadeUp, viewportOnce, stagger } from "../../lib/motion";
import { DESIGN_COST } from "../../lib/product";
import { SectionIntro, DemoChip } from "./shared";

const DesignToCostEngine = () => {
    const [changed, setChanged] = useState(false);
    const reduce = useReducedMotion();

    return (
        <section
            id="design-to-cost"
            data-testid="design-to-cost-section"
            className="relative bg-ink py-24 lg:py-32"
        >
            <div className="mx-auto max-w-[1600px] px-6 md:px-10 lg:px-16">
                <SectionIntro
                    testid="designtocost-intro"
                    eyebrow="DESIGN-TO-COST"
                    title="What happens when you change the design?"
                />

                <motion.div
                    variants={stagger}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="mt-14 grid items-stretch gap-6 lg:grid-cols-[1fr_auto_1.2fr]"
                >
                    <motion.figure
                        variants={fadeUp}
                        data-testid="designtocost-before"
                        className="group relative min-w-0 border border-white/12 bg-[#0C1428]"
                    >
                        <div className="overflow-hidden">
                            <img
                                src={DESIGN_COST.before.img}
                                alt={DESIGN_COST.before.alt}
                                loading="lazy"
                                className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-[1.03]"
                            />
                        </div>
                        <figcaption className="flex items-center justify-between border-t border-white/10 px-4 py-3">
                            <span className="font-mono text-[13px] tracking-[0.22em] text-white">
                                {DESIGN_COST.before.finish}
                            </span>
                            <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                {DESIGN_COST.before.tag}
                            </span>
                        </figcaption>
                    </motion.figure>

                    <motion.div
                        variants={fadeUp}
                        className="flex flex-row flex-wrap items-center justify-center gap-4 lg:flex-col lg:gap-6 lg:px-2"
                    >
                        <span className="font-mono text-[13px] tracking-[0.22em] text-cad">
                            USER CHANGE
                        </span>
                        <div
                            role="group"
                            aria-label="Material change demonstration"
                            className="flex border border-white/15"
                        >
                            <button
                                type="button"
                                data-testid="material-switch-plaster"
                                aria-pressed={!changed}
                                onClick={() => setChanged(false)}
                                className={`px-4 py-2.5 text-[14px] transition-colors duration-300 ${
                                    !changed
                                        ? "bg-white/10 font-semibold text-white"
                                        : "text-mist hover:text-white"
                                }`}
                            >
                                White Plaster
                            </button>
                            <button
                                type="button"
                                data-testid="material-switch-facebrick"
                                aria-pressed={changed}
                                onClick={() => setChanged(true)}
                                className={`px-4 py-2.5 text-[14px] transition-colors duration-300 ${
                                    changed
                                        ? "bg-accent font-semibold text-white"
                                        : "text-mist hover:text-white"
                                }`}
                            >
                                Face Brick
                            </button>
                        </div>
                        <span className="hidden h-6 w-px bg-white/15 lg:block" />
                        <span className="font-mono text-[13px] tracking-[0.22em] text-cad lg:rotate-90">
                            {changed ? "→ FACE BRICK" : "NO PENDING CHANGE"}
                        </span>
                    </motion.div>

                    <motion.div
                        variants={fadeUp}
                        data-testid="designtocost-analysis"
                        className="relative border border-white/12 bg-[#0C1428] px-6 py-6"
                    >
                        <div className="flex items-center justify-between">
                            <AnimatePresence mode="wait" initial={false}>
                                <motion.span
                                    key={changed ? "detected" : "idle"}
                                    initial={{ opacity: 0 }}
                                    animate={{ opacity: 1 }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: reduce ? 0 : 0.3,
                                    }}
                                    data-testid="designtocost-status"
                                    className={`inline-flex items-center gap-2 px-3 py-1.5 font-mono text-[13px] tracking-[0.22em] ${
                                        changed
                                            ? "bg-accent text-white"
                                            : "border border-white/20 text-mist"
                                    }`}
                                >
                                    {changed ? (
                                        <>
                                            <span className="h-1.5 w-1.5 rounded-full bg-white" />
                                            DESIGN CHANGE DETECTED
                                        </>
                                    ) : (
                                        "ANALYSIS IDLE"
                                    )}
                                </motion.span>
                            </AnimatePresence>
                            <DemoChip>DEMO DATA</DemoChip>
                        </div>

                        {changed ? (
                            <motion.div
                                key="analysis"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                transition={{
                                    duration: reduce ? 0 : 0.5,
                                    delay: reduce ? 0 : 0.15,
                                }}
                            >
                                <dl className="mt-6 space-y-3">
                                    {DESIGN_COST.analysis.map((row, i) => (
                                        <motion.div
                                            key={row.label}
                                            initial={{
                                                opacity: reduce ? 1 : 0,
                                                x: reduce ? 0 : -12,
                                            }}
                                            animate={{ opacity: 1, x: 0 }}
                                            transition={{
                                                duration: reduce ? 0 : 0.45,
                                                delay: reduce ? 0 : 0.2 + i * 0.12,
                                            }}
                                            className="flex items-center justify-between gap-4 border-b border-white/[0.07] pb-3"
                                        >
                                            <dt className="font-mono text-[13px] tracking-[0.22em] text-cad">
                                                {row.label}
                                            </dt>
                                            <dd className="text-[15px] text-white/90">
                                                {row.value}
                                            </dd>
                                        </motion.div>
                                    ))}
                                </dl>
                                <div className="mt-6 flex items-end justify-between border border-emerald-400/40 bg-emerald-400/[0.06] px-5 py-4">
                                    <div>
                                        <p className="font-mono text-[13px] tracking-[0.22em] text-mist">
                                            COST IMPACT
                                        </p>
                                        <p
                                            data-testid="designtocost-impact"
                                            className="mt-1 font-display text-3xl font-semibold text-emerald-400 md:text-4xl"
                                        >
                                            {DESIGN_COST.impact}
                                        </p>
                                    </div>
                                    <p className="max-w-[180px] text-right font-mono text-[13px] leading-relaxed tracking-[0.18em] text-cad">
                                        VISUAL DESIGN EXAMPLE ONLY — NOT A LIVE
                                        PRICE
                                    </p>
                                </div>
                            </motion.div>
                        ) : (
                            <motion.p
                                key="empty"
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                data-testid="designtocost-empty"
                                className="mt-6 border border-dashed border-white/15 px-5 py-10 text-center text-[15px] text-cad"
                            >
                                Switch the material to Face Brick to see how a
                                design change flows through to cost.
                            </motion.p>
                        )}
                    </motion.div>
                </motion.div>

                <motion.p
                    variants={fadeUp}
                    initial="hidden"
                    whileInView="show"
                    viewport={viewportOnce}
                    className="mt-10 max-w-2xl text-[16px] leading-relaxed text-mist"
                >
                    BuildEase connects design decisions to potential cost impact
                    using verified project information and local pricing where
                    available.
                </motion.p>
            </div>
        </section>
    );
};

export default DesignToCostEngine;
